import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isOrder = params.get("type") === "order";
  const { clear } = useCart();
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const cleared = useRef(false);

  const statusPath = isOrder ? "/checkout/status/" : "/payments/status/";

  const poll = useCallback(
    async (attempt) => {
      if (attempt > 8) {
        setState("timeout");
        return;
      }
      try {
        const { data } = await api.get(`${statusPath}${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (isOrder && !cleared.current) {
            cleared.current = true;
            clear();
            localStorage.removeItem("drift_ref");
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        // keep trying
      }
      setTimeout(() => poll(attempt + 1), 2000);
    },
    [sessionId, statusPath, isOrder, clear]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setState("error");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <div className="bg-[#04070A] min-h-screen flex flex-col" data-testid="payment-success-page">
      <Nav />
      <section className="flex-1 flex items-center justify-center px-6 pt-24 pb-24">
        <div className="max-w-md text-center">
          {state === "polling" && (
            <>
              <Loader2 className="w-14 h-14 text-orange-500 animate-spin mx-auto" />
              <h1 className="font-display text-4xl uppercase tracking-tight mt-8">Confirming…</h1>
              <p className="text-slate-400 mt-4">Hang tight while we confirm your payment.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-orange-500 mx-auto" data-testid="success-icon" />
              <h1 className="font-display text-5xl uppercase tracking-tight mt-8">
                {isOrder ? "Order Confirmed" : "You're In"}
              </h1>
              <p className="text-slate-400 mt-4">
                {isOrder
                  ? "Payment received — a receipt is on its way to your inbox. We'll get your order poured, packed and shipped."
                  : "Welcome to the Drift Tackle Club. Your first box is on the way — keep an eye on your inbox for a receipt from Stripe."}
              </p>
              <Link
                to="/shop"
                className="inline-block mt-8 bg-orange-500 text-[#04070A] font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-orange-600 transition-colors"
                data-testid="success-shop-link"
              >
                {isOrder ? "Keep Shopping" : "Browse the Shop"}
              </Link>
            </>
          )}
          {(state === "timeout" || state === "error") && (
            <>
              <XCircle className="w-14 h-14 text-slate-500 mx-auto" />
              <h1 className="font-display text-4xl uppercase tracking-tight mt-8">
                {state === "timeout" ? "Still Processing" : "Something Went Wrong"}
              </h1>
              <p className="text-slate-400 mt-4">
                {state === "timeout"
                  ? "Your payment is taking a moment. If you were charged, you're all set — check your email for a receipt."
                  : "We couldn't confirm your payment. If you were charged, please contact us."}
              </p>
              <Link
                to={isOrder ? "/shop" : "/club"}
                className="inline-block mt-8 border border-white/20 font-mono-plex text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-white/5 transition-colors"
              >
                {isOrder ? "Back to the Shop" : "Back to the Club"}
              </Link>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
